import { type LimitedPartnerInsight } from "@vega-api/generated/types/limitedPartnerInsight";
import { type DataGridColDef } from "@vega-core/components/DataGrid/types";
import { useColumnToggle } from "@vega-core/components/DataGrid/useColumnToggle";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { formatMoneyShort, isMoneyValid } from "@vega-core/utils/formatMoney";

// Every colId doubles as the backend sort field for /insights/limited-partners
// — OrganisationsTable sends the sorted column's colId as the `sort` param.
export const useOrganisationsTableColumns = () => {
  const columns: DataGridColDef<LimitedPartnerInsight>[] = [
    {
      colId: "limitedPartnerName",
      headerName: m.coInvestments_insights_col_organisation(),
      field: "limitedPartnerName",
      minWidth: 240,
      pinned: "left",
      flex: 1,
    },
    {
      colId: "assetsUnderManagement",
      headerName: m.coInvestments_insights_col_aum(),
      valueGetter: ({ data }) => data?.assetsUnderManagement?.amount,
      valueFormatter: ({ data }) => {
        const assetsUnderManagement = data?.assetsUnderManagement;

        return isMoneyValid(assetsUnderManagement)
          ? formatMoneyShort(assetsUnderManagement)
          : EMPTY_CELL_VALUE;
      },
      toggleable: true,
    },
    {
      colId: "geography",
      headerName: m.coInvestments_insights_col_geography(),
      field: "geography",
      valueFormatter: ({ value }) =>
        typeof value === "string" && value !== "" ? value : EMPTY_CELL_VALUE,
      toggleable: true,
    },
    {
      colId: "primaryCommitment",
      headerName: m.coInvestments_insights_col_primaryCommitment(),
      valueGetter: ({ data }) => data?.primaryCommitment.amount,
      valueFormatter: ({ data }) => formatMoneyShort(data?.primaryCommitment),
      toggleable: true,
    },
    {
      colId: "coinvestments",
      headerName: m.coInvestments_insights_col_coinvestments(),
      field: "coinvestments",
      toggleable: true,
    },
    {
      colId: "coinvestmentTotal",
      headerName: m.coInvestments_insights_col_coinvestmentTotal(),
      valueGetter: ({ data }) => data?.coinvestmentTotal.amount,
      valueFormatter: ({ data }) => formatMoneyShort(data?.coinvestmentTotal),
      toggleable: true,
    },
    {
      colId: "activityProspecting",
      headerName: m.coInvestments_insights_col_prospecting(),
      field: "activityProspecting",
      toggleable: true,
    },
    {
      colId: "declinedCoinvestments",
      headerName: m.coInvestments_insights_col_declined(),
      field: "declinedCoinvestments",
      toggleable: true,
    },
  ];

  return useColumnToggle<LimitedPartnerInsight>(columns);
};
